import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { 
  BaseAdminScreen, 
  AdminEmptyScreenLayout, 
  LoadingSpinner,
  SearchBar,
  InlineEmptyMessage,
  EventDetailsModal,
} from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useEvents } from '@/hooks/admin/useEvents';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS, EMPTY_MESSAGES } from '@/constants/admin';
import { getEventStatusColor } from '@/utils/admin/events/status';
import { EVENT_VIEW_TYPES, IEvent } from '@/types/admin/events';
import { EventsCalendar } from '@/components/admin/events/EventsCalendar';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopEventsView } from './desktop/DesktopEventsView';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';

type EventsScreenProps = StackScreenProps<AdminStackParamList, 'Events'>;

export const EventsScreen: React.FC<EventsScreenProps> = ({
  navigation,
}) => {
  const { layoutMode } = useResponsive();
  const { showApiError } = useErrorToast();

  const {
    data,
    tableRows,
    monthlyEvents,
    selectedMonth,
    selectedEvent,
    isLoading,
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    view,
    searchQuery,
    updateView,
    updateSearchQuery,
    updateSelectedMonth,
    updateSelectedEvent,
    calendarKeys,
    formatAttendeeLimit,
  } = useEvents();

  // Show toast for API errors
  useEffect(() => {
    if (enhancedError) {
      showApiError(enhancedError.originalError, enhancedError.userFriendlyMessage);
    }
  }, [enhancedError, showApiError]);

  const renderViewTabs = () => (
    <View style={styles.viewTabs}>
      <TouchableOpacity
        style={[styles.viewTab, view === EVENT_VIEW_TYPES.CALENDAR && styles.activeViewTab]}
        onPress={() => updateView(EVENT_VIEW_TYPES.CALENDAR)}
      >
        <Text style={[styles.viewTabText, view === EVENT_VIEW_TYPES.CALENDAR && styles.activeViewTabText]}>
          Calendar
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.viewTab, view === EVENT_VIEW_TYPES.LIST && styles.activeViewTab]}
        onPress={() => updateView(EVENT_VIEW_TYPES.LIST)}
      >
        <Text style={[styles.viewTabText, view === EVENT_VIEW_TYPES.LIST && styles.activeViewTabText]}>
          List
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderCalendarView = () => {
    return (
      <EventsCalendar
        monthlyEvents={monthlyEvents}
        selectedMonth={selectedMonth}
        onMonthChange={updateSelectedMonth}
        onEditEvent={handleEditEvent}
      />
    );
  };

  const renderListView = () => {
    const hasFilteredData = filteredRows.length > 0;
    if (hasFilteredData) {
      return (
        <FlatList
          data={filteredRows}
          keyExtractor={(item) => item.id.toString()}
          showsVerticalScrollIndicator={false}
          style={styles.listContainer}
          renderItem={({ item: event }) => (
            <TouchableOpacity
              style={styles.eventCard}
              onPress={() => handleListEventPress(event.id)}
            >
              <View style={styles.eventCardHeader}>
                <Text style={styles.eventName}>{event.name}</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(event.status) }]}>
                  <Text style={styles.statusText}>{event.status}</Text>
                </View>
              </View>
              <View style={styles.eventCardBody}>
                <Text style={styles.eventLocation}>{event.location}</Text>
                <Text style={styles.eventTime}>
                  {new Date(event.startTime).toLocaleDateString()} • {new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Text>
                <Text style={styles.eventRsvps}>
                  {event.n_rsvps} RSVPs • {formatAttendeeLimit(event.rsvpLimit)} max
                </Text>
              </View>
            </TouchableOpacity>
          )}
        />
      );
    }

    // Show contextual empty message for search results
    if (hasSearchQuery) {
      return (
        <InlineEmptyMessage
          title={EMPTY_MESSAGES.events.noResults(searchQuery).title}
          message={EMPTY_MESSAGES.events.noResults(searchQuery).message}
          icon={EMPTY_MESSAGES.events.noResults(searchQuery).icon}
          containerStyle={styles.noResultsContainer}
          titleStyle={styles.noResultsTitle}
          messageStyle={styles.noResultsMessage}
        />
      );
    }

    // Show default empty state when no search is active
    return renderEmptyMessage();
  };

  const getStatusColor = (status: string) => getEventStatusColor(status);

  const renderEmptyMessage = () => {
    const cfg = EMPTY_MESSAGES.events.default;
    return (
      <InlineEmptyMessage
        title={cfg.title}
        message={cfg.message}
        icon={cfg.icon}
        containerStyle={styles.emptyMessageContainer}
        titleStyle={styles.emptyMessageText}
        messageStyle={styles.emptyMessageSubtext}
      />
    );
  };

  // Handle create event functionality
  const handleAddEvent = useCallback(() => {
    navigation.navigate('CreateEvent');
  }, [navigation]);

  // Determine if we should show data or empty state
  const hasData = data?.data && data.data.length > 0;
  const hasSearchQuery = searchQuery.trim() !== '';
  
  // Filter table rows by selected month/year for List view (now centralized in hook; keep as fallback)
  const filteredRows = useMemo(() => {
    const selectedMonthDate = new Date(selectedMonth);
    return tableRows.filter((event) => {
      const start = new Date(event.startTime || '');
      return (
        start.getMonth() === selectedMonthDate.getMonth() &&
        start.getFullYear() === selectedMonthDate.getFullYear()
      );
    });
  }, [tableRows, selectedMonth]);

  // Modal state for list view selections
  const [modalVisible, setModalVisible] = useState(false);

  const handleListEventPress = useCallback((eventId: number) => {
    const fullEvent = (data?.data || []).find((e: IEvent) => e.id === eventId);
    if (fullEvent) {
      updateSelectedEvent(fullEvent);
      setModalVisible(true);
    }
  }, [data, updateSelectedEvent]);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    updateSelectedEvent(null);
  }, [updateSelectedEvent]);

  const handleEditEvent = useCallback((eventId: number) => {
    setModalVisible(false);
    updateSelectedEvent(null);
    navigation.navigate('EditEvent', { eventId });
  }, [navigation, updateSelectedEvent]);
  
  // Show error state if we have a critical error and no data and no active search
  const showErrorState = enhancedError && !hasData && !isLoading && !hasSearchQuery;
  
  // Show loading state if we're loading and have no data yet and no active search
  const showLoadingState = isLoading && !hasData && !showErrorState && !hasSearchQuery;

  // Render error state component
  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={ADMIN_COLORS.ERROR} />
      <Text style={styles.errorTitle}>Unable to Load Events</Text>
      <Text style={styles.errorMessage}>
        {enhancedError?.userFriendlyMessage || 'Something went wrong while loading the events.'}
      </Text>
      
      {retryCount > 0 && (
        <Text style={styles.retryInfo}>
          Retry attempts: {retryCount}
        </Text>
      )}
      
      {canRetry && (
        <TouchableOpacity style={styles.retryButton} onPress={manualRetry}>
          <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color="#FFFFFF" />
          <Text style={styles.retryButtonText}>Try Again</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  // Render content based on view mode
  const renderContent = () => {
    if (showLoadingState) {
      return <LoadingSpinner message="Loading events..." />;
    }
    
    if (showErrorState) {
      return renderErrorState();
    }

    return (
      <View style={styles.contentContainer}>
        {renderViewTabs()}
        
        {/* Search Bar - Only show for List view */}
        {view === EVENT_VIEW_TYPES.LIST && (
          <View style={styles.searchContainer}>
            <SearchBar
              placeholder="Search events..."
              value={searchQuery}
              onChangeText={updateSearchQuery}
            />
          </View>
        )}

        {view === EVENT_VIEW_TYPES.CALENDAR ? (
          renderCalendarView()
        ) : (
          renderListView()
        )}
      </View>
    );
  };

  // Desktop web rendering
  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="EventsScreenDesktop">
          <DesktopEventsView
            monthlyEvents={monthlyEvents}
            selectedMonth={selectedMonth}
            selectedEvent={selectedEvent as IEvent | null}
            searchQuery={searchQuery}
            onSearchChange={updateSearchQuery}
            onMonthChange={updateSelectedMonth}
            onSelectEvent={(e) => updateSelectedEvent(e)}
            onCreateEvent={handleAddEvent}
            onEditEvent={handleEditEvent}
          />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="EventsScreen">
      <DataTableProvider tableKey="admin:events" defaults={{ paginationModel: { page: 0, pageSize: 25 } }}>
        <BaseAdminScreen
          screenName="Events"
          navigation={navigation}
          showPlaceholder={false}
          scrollEnabled={false}
          contentContainerStyle={styles.baseScreenContent}
        >
          <AdminEmptyScreenLayout
            title="Events"
            emptyStateType="events"
            onAddPress={handleAddEvent}
            showAddButton={true}
            buttonType="add"
            hasContent={true}
            disableScrollView={true}
          >
            {renderContent()}
          </AdminEmptyScreenLayout>

          {/* Event Details Modal (for List view selection) */}
          <AdminErrorBoundary context="EventDetailsModal">
            <EventDetailsModal
              visible={modalVisible}
              event={selectedEvent as IEvent | null}
              onClose={handleCloseModal}
              onEdit={handleEditEvent}
            />
          </AdminErrorBoundary>
        </BaseAdminScreen>
      </DataTableProvider>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  contentContainer: {
    flex: 1,
  },
  viewTabs: {
    flexDirection: 'row',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    padding: 4,
    marginBottom: 16,
  },
  viewTab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  activeViewTab: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  viewTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  activeViewTabText: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  searchContainer: {
    marginBottom: 16,
  },
  

  
  // List view styles
  listContainer: {
    flex: 1,
  },
  eventCard: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  eventCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  eventName: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
    marginRight: 12,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  eventCardBody: {
    gap: 4,
  },
  eventLocation: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  eventTime: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  eventRsvps: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  
  // Error state styles
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
    maxWidth: 300,
  },
  retryInfo: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },

  // Empty message styles
  emptyMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -120,
    paddingHorizontal: 24,
  },
  emptyMessageIcon: {
    marginBottom: 16,
  },
  emptyMessageText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessageSubtext: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
  noResultsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: -120,
    paddingHorizontal: 20,
  },
  noResultsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
    textAlign: 'center',
  },
  noResultsMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
});
